import { Temporal } from '@js-temporal/polyfill';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState, type ReactNode } from 'react';

import { venueInstructionsQuery } from '../api/instructions.ts';
import { rosterPageQuery } from '../api/roster.ts';
import { lapsingInductionsQuery, venueJobsQuery, venueQuotesQuery } from '../api/venue-page.ts';
import { roomsQuery, type Venue } from '../api/venues.ts';
import { Button } from '../components/button.tsx';
import { icons } from '../components/icons.ts';
import { movements, weekGrid, weekOf, WHOLE_VENUE, type Booking } from '../venue-week.ts';
import { dayName, initials } from './roster-bars.tsx';

// CB-REQ VEN-01 at v1.9104, the operator's venue page design of 2026-10-26 (docs/designs/venue-page
// .md §2): the Overview reads, adding no data. The week by room — the calendar week in the venue's
// zone, a cell per booking: ours solid, short outlined red, the venue's own grey, a pencilled quote
// dashed, each opening its job or quote; movements in and out with their dock booking; and to the
// side, everyone here today, the standing instructions and the contacts. Each read is made only by
// someone who may: without it, its part is left out.

const hhmm = (local: string) => local.slice(11, 16);

export function VenueOverview({
  venue: v,
  capabilities,
  tenantZone,
  contacts,
}: {
  venue: Venue;
  capabilities: readonly string[];
  tenantZone: string;
  /** The contacts panel, managed where it is defined. */
  contacts: ReactNode;
}) {
  const may = (key: string) => capabilities.includes(key);
  const zone = v.timeZone ?? tenantZone;
  const today = Temporal.Now.zonedDateTimeISO(zone).toPlainDate().toString();
  const [offset, setOffset] = useState(0);
  const week = weekOf(today, zone, offset);
  const from = week.from.toString();
  const to = week.to.toString();

  const rooms = useQuery(roomsQuery(v.id));
  const jobs = useQuery({ ...venueJobsQuery(v.id, from, to), enabled: may('jobs.view') });
  const quotes = useQuery({ ...venueQuotesQuery(v.id, from, to), enabled: may('quotes.view') });
  const grid = weekGrid(week, zone, jobs.data ?? [], quotes.data ?? []);
  const rows = [
    ...(rooms.data ?? []).map((r) => ({ id: r.id, name: r.name })),
    ...(grid.has(WHOLE_VENUE) ? [{ id: WHOLE_VENUE, name: 'No room' }] : []),
  ];
  const moves = movements(week, jobs.data ?? []);

  return (
    <div className="venue-overview">
      <div className="flex min-w-0 flex-col gap-3">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="section-title flex-1 pb-0!">Week</h2>
          <div className="flex items-center gap-1">
            <Button
              variant="quiet"
              className="small-btn"
              aria-label="Week before"
              onClick={() => setOffset(offset - 1)}
            >
              <icons.previous size={16} aria-hidden />
            </Button>
            <Button variant="quiet" className="small-btn" onClick={() => setOffset(0)}>
              This week
            </Button>
            <Button
              variant="quiet"
              className="small-btn"
              aria-label="Week after"
              onClick={() => setOffset(offset + 1)}
            >
              <icons.next size={16} aria-hidden />
            </Button>
          </div>
        </div>
        <ul className="flex flex-wrap gap-4 text-[11px] text-muted" aria-label="Key">
          <li className="flex items-center gap-1.5">
            <span className="week-block week-ours h-3 w-4" /> Ours
          </li>
          <li className="flex items-center gap-1.5">
            <span className="week-block week-pencil h-3 w-4" /> Pencilled
          </li>
          <li className="flex items-center gap-1.5">
            <span className="week-block week-short h-3 w-4" /> Short
          </li>
          <li className="flex items-center gap-1.5">
            <span className="week-block week-venue h-3 w-4" /> Venue’s own
          </li>
        </ul>
        {may('jobs.view') ? (
          <div className="overflow-x-auto">
            <table className="week-grid">
              <caption className="sr-only">
                Bookings by room, {dayName(week.days[0]!)} to {dayName(week.days[6]!)}
              </caption>
              <thead>
                <tr>
                  <th scope="col">Room</th>
                  {week.days.map((d) => (
                    <th key={d} scope="col" aria-current={d === today ? 'date' : undefined}>
                      {dayName(d)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="text-muted">
                      No rooms recorded.
                    </td>
                  </tr>
                ) : null}
                {rows.map((r) => (
                  <tr key={r.id}>
                    <th scope="row">{r.name}</th>
                    {week.days.map((d, i) => (
                      <td key={d}>
                        {(grid.get(r.id)?.[i] ?? []).map((b, j) => (
                          <Block key={j} booking={b} />
                        ))}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {may('jobs.view') ? (
          <>
            <h2 className="section-title mt-2 pb-0!">Movements</h2>
            {moves.length === 0 ? (
              <p className="text-sm text-muted">No gear in or out this week.</p>
            ) : (
              <table className="data-table text-sm">
                <thead>
                  <tr>
                    <th scope="col">
                      <span className="sr-only">In or out</span>
                    </th>
                    <th scope="col">Gear</th>
                    <th scope="col">When</th>
                    <th scope="col">Job</th>
                    <th scope="col">Dock</th>
                  </tr>
                </thead>
                <tbody>
                  {moves.map((m) => {
                    const local = Temporal.Instant.from(m.at).toZonedDateTimeISO(zone);
                    const day = local.toPlainDate().toString();
                    return (
                      <tr key={`${m.job.id}-${m.way}`}>
                        <td>
                          <span className={`tag ${m.way === 'in' ? 'tag-accent' : ''}`}>
                            {m.way === 'in' ? 'In' : 'Out'}
                          </span>
                        </td>
                        <td>
                          {m.job.lines} {m.job.lines === 1 ? 'line' : 'lines'}
                          {m.done ? '' : ' · due'}
                        </td>
                        <td>
                          {day === today ? 'Today' : dayName(day)}{' '}
                          {local.toPlainTime().toString({ smallestUnit: 'minute' })}
                        </td>
                        <td>
                          <Link to="/jobs/$jobId" params={{ jobId: m.job.id }} className="link">
                            {m.job.reference}
                          </Link>
                        </td>
                        <td>
                          {m.job.dockBooking ? (
                            (m.job.dockBooking.reference ?? 'Booked')
                          ) : (
                            <span className="font-semibold text-accent-2-700">Not booked</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </>
        ) : null}
      </div>

      <aside className="flex flex-col gap-4">
        {may('shifts.view') ? <HereToday venue={v} zone={zone} today={today} may={may} /> : null}
        <Instructions venueId={v.id} />
        <section className="side-panel">
          {contacts}
          {v.musterPoint ? (
            <p className="text-sm">
              <span className="text-muted">Muster point:</span> {v.musterPoint}
            </p>
          ) : null}
          {v.notes ? <p className="text-sm whitespace-pre-wrap">{v.notes}</p> : null}
        </section>
      </aside>
    </div>
  );
}

function Block({ booking: b }: { booking: Booking }) {
  if (b.kind === 'pencil') {
    return (
      <Link
        to="/quotes/$quoteId"
        params={{ quoteId: b.quote.id }}
        className="week-block week-pencil"
        title={`${b.quote.reference}, pencilled`}
      >
        {b.quote.reference} pencilled
      </Link>
    );
  }
  const word = { ours: '', short: ' · short', venue: ' · venue’s own' }[b.kind];
  return (
    <Link
      to="/jobs/$jobId"
      params={{ jobId: b.job.id }}
      className={`week-block week-${b.kind}`}
      title={`${b.job.reference} ${b.job.name}${word}`}
    >
      {b.job.reference} {b.job.name}
      {word}
    </Link>
  );
}

/** CRW-11: everyone with a shift at the venue today, from the roster's read filtered to it. */
function HereToday({
  venue,
  zone,
  today,
  may,
}: {
  venue: Venue;
  zone: string;
  today: string;
  may: (key: string) => boolean;
}) {
  const start = Temporal.PlainDate.from(today).toZonedDateTime({ timeZone: zone });
  const page = useQuery(
    rosterPageQuery(start.toInstant().toString(), start.add({ days: 1 }).toInstant().toString(), {
      jobIds: [],
      venueIds: [venue.id],
    }),
  );
  const lapsing = useQuery({ ...lapsingInductionsQuery(venue.id), enabled: may('crew.view') });
  const here = (page.data?.shifts ?? []).filter(
    // The roster's venue filter takes shifts that report here or carry a task here (OPS-03).
    (s) => s.crewMember && s.state !== 'declined' && s.state !== 'cancelled',
  );
  const roomOf = (taskIds: readonly string[]) =>
    page.data?.tasks.find((t) => taskIds.includes(t.id) && t.room)?.room?.name ?? null;
  return (
    <section className="side-panel" aria-labelledby="here-today">
      <div className="flex items-center">
        <h2 id="here-today" className="section-title flex-1 pb-0!">
          Here today
        </h2>
        <span className="text-xs text-muted">{here.length} crew</span>
      </div>
      {here.length === 0 ? <p className="text-sm text-muted">Nobody on shift here today.</p> : null}
      <ul className="flex flex-col">
        {here.map((s) => {
          const lapses = lapsing.data?.find((i) => i.crewMemberId === s.crewMember!.id);
          const room = roomOf(s.tasks.map((t) => t.id));
          return (
            <li key={s.id} className="side-row">
              <span className="roster-mono" aria-hidden="true">
                {initials(s.crewMember!.name)}
              </span>
              <span className="flex-1">
                {s.crewMember!.name}
                {s.role ? ` · ${s.role.name}` : ''}
              </span>
              {lapses ? (
                <span className="tag tag-accent-2">Induction {dayName(lapses.expiresOn)}</span>
              ) : (
                <span className="text-xs text-muted">
                  {room ? `${room} · ` : ''}to {hhmm(s.window.localEnd)}
                </span>
              )}
            </li>
          );
        })}
      </ul>
      <Link to="/roster" search={{ venue: venue.id }} className="link text-sm">
        {here.length > 0 ? `All ${here.length} in the roster` : 'The roster'}
      </Link>
    </section>
  );
}

/** VEN-09: the venue's standing instructions, each with the room it is for. */
function Instructions({ venueId }: { venueId: string }) {
  const instructions = useQuery(venueInstructionsQuery(venueId));
  const live = (instructions.data ?? []).filter((i) => !i.retiredAt);
  return (
    <section className="side-panel" aria-labelledby="overview-instructions">
      <div className="flex items-center">
        <h2 id="overview-instructions" className="section-title flex-1 pb-0!">
          Instructions
        </h2>
        <span className="text-xs text-muted">{live.length}</span>
      </div>
      {live.length === 0 ? <p className="text-sm text-muted">None recorded.</p> : null}
      <ul className="flex flex-col">
        {live.map((i) => (
          <li key={i.id} className="side-row">
            <span className="flex-1">{i.text}</span>
            <span className="text-xs text-muted">{i.room ? i.room.name : 'Venue'}</span>
          </li>
        ))}
      </ul>
      <Link
        to="/venues/$venueId"
        params={{ venueId }}
        search={{ tab: 'rules' }}
        className="link text-sm"
      >
        In Rules
      </Link>
    </section>
  );
}
